/**
 * Rewrites Xtream Codes and M3U stream URLs with the active pool credentials of the client.
 */
export function rewriteStreamCredentials(rawUrl?: string, serverUser?: string, serverPass?: string): string {
  if (!rawUrl) return '';
  if (!serverUser || !serverPass) return rawUrl;

  let url = rawUrl.trim();

  // Padrão Xtream Path: /live/USER/PASS/ID, /movie/USER/PASS/ID, /series/USER/PASS/ID, /hls/USER/PASS/ID
  url = url.replace(
    /(\/(?:live|movie|series|hls)\/)[^\/]+\/[^\/]+(\/[^\/\?#]+)/gi,
    `$1${encodeURIComponent(serverUser)}/${encodeURIComponent(serverPass)}$2`
  );

  // Padrão Xtream Raiz: http://host:port/USER/PASS/ID
  url = url.replace(
    /^(https?:\/\/[^\/]+)\/[^\/]+\/[^\/]+(\/\d+(?:\.[a-zA-Z0-9]+)?)/gi,
    `$1/${encodeURIComponent(serverUser)}/${encodeURIComponent(serverPass)}$2`
  );

  // Padrão Query Params: ?username=...&password=...
  if (url.includes('username=') && url.includes('password=')) {
    url = url.replace(/([?&]username=)[^&]+/gi, `$1${encodeURIComponent(serverUser)}`);
    url = url.replace(/([?&]password=)[^&]+/gi, `$1${encodeURIComponent(serverPass)}`);
  }

  return url;
}

/**
 * Retorna se o ambiente de execução atual é seguro (HTTPS)
 */
export function isHttpsContext(): boolean {
  if (typeof window === 'undefined') return false;
  return window.location.protocol === 'https:';
}

/**
 * IPTV Stream Proxy Helper
 * Rewrites external streams through local Vite/Express proxy to bypass CORS, Mixed Content, geo-restrictions and codec blocks.
 */
export function getProxiedStreamUrl(rawUrl?: string, serverUser?: string, serverPass?: string, deviceId?: string): string {
  if (!rawUrl) return '';

  const processedUrl = (serverUser && serverPass) ? rewriteStreamCredentials(rawUrl, serverUser, serverPass) : rawUrl.trim();

  if (
    processedUrl.startsWith('https://stream.mariozeli.com.br/proxy') ||
    processedUrl.startsWith('/api/proxy') ||
    processedUrl.startsWith('blob:') ||
    processedUrl.startsWith('data:')
  ) {
    return processedUrl;
  }

  const devParam = deviceId ? `&dev_id=${encodeURIComponent(deviceId)}` : '';

  // Em ambiente local de desenvolvimento, usa o proxy do Vite
  const isDev = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname.includes('127.0.0.1'));
  if (isDev) {
    return `/api/proxy?url=${encodeURIComponent(processedUrl)}${devParam}`;
  }

  // Em produção, usa a VPS Oracle dedicada de alta performance (10 TB de tráfego grátis, zero consumo na HostGator)
  return `https://stream.mariozeli.com.br/proxy?url=${encodeURIComponent(processedUrl)}${devParam}`;
}

/**
 * Checks if a stream is HLS based on extension or parameters
 */
export function isHlsStream(url?: string): boolean {
  if (!url) return false;
  const lower = url.toLowerCase();
  return lower.includes('.m3u8') || lower.includes('output=m3u8');
}

/**
 * Checks if a stream is MPEG-TS based on extension
 */
export function isMpegTsStream(url?: string): boolean {
  if (!url) return false;
  const lower = url.toLowerCase();
  return lower.includes('.ts') || lower.includes('output=ts');
}

/**
 * Converte URLs MPEG-TS ou Xtream brutas para HLS (.m3u8), essencial para o player nativo do iOS
 */
export function convertToHlsUrl(rawUrl?: string): string {
  if (!rawUrl) return '';
  let url = rawUrl.trim();

  // Substitui extensão .ts no final da URL ou antes da query string
  url = url.replace(/\.ts(\?|$)/i, '.m3u8$1');

  // Substitui query parameter output=ts por output=m3u8
  url = url.replace(/([?&])output=ts(&|$)/gi, '$1output=m3u8$2');

  // Se já tem .m3u8, não precisa adicionar novamente
  if (url.toLowerCase().includes('.m3u8')) return url;

  // Padrão Xtream /live/user/pass/id ou /user/pass/id sem extensão -> adiciona .m3u8
  if (url.match(/\/(?:live\/)?[^\/]+\/[^\/]+\/\d+(\?|$)/i)) {
    url = url.replace(/(\/(?:live\/)?[^\/]+\/[^\/]+\/\d+)(\?|$)/i, '$1.m3u8$2');
  }

  return url;
}

/**
 * Troca o domínio base de uma URL Xtream/IPTV por outro host da lista de DNS
 */
export function replaceServerHost(url: string, newHostBase: string): string {
  try {
    const original = new URL(url);
    const target = new URL(newHostBase.startsWith('http') ? newHostBase : `http://${newHostBase}`);
    original.protocol = target.protocol;
    original.host = target.host;
    original.port = target.port;
    return original.toString();
  } catch {
    return url;
  }
}
